import arrayMutators from 'final-form-arrays'
import { Button } from "primereact/button"
import { Checkbox } from "primereact/checkbox"
import { Dropdown } from "primereact/dropdown"
import { Fieldset } from 'primereact/fieldset'
import { InputText } from "primereact/inputtext"
import { classNames } from "primereact/utils"
import { Field, Form } from "react-final-form"
import { FieldArray } from "react-final-form-arrays"
import { ConfirmationQuestion, InterviewNode, QuestionType } from "../../../../../../CandidatePortal/scenes/Interview/Chatbot/interface"

import OnChange from "../../../../../../utils/OnChange"
import styles from "../styles.module.css"
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../../store'
import { InputNumber } from 'primereact/inputnumber'

interface ConfirmationFormProps {
  actionOnSubmit: any
  actionOnDelete: any
  intentNode: ConfirmationQuestion
  firstNode: InterviewNode
  lastNode:InterviewNode
  intentNameList?: string[]
  isUpdate?: boolean
  autosave: boolean
  onQuestionTypeChange: (newQuestionType: any) => void
}

const ConfirmationForm = (props: ConfirmationFormProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { actionOnSubmit, actionOnDelete, intentNode, firstNode, lastNode, intentNameList, isUpdate = false, autosave, onQuestionTypeChange } = props

  let initialValues: ConfirmationQuestion = !!intentNode ? { ...intentNode, } : {} as ConfirmationQuestion

  const validate = (data: InterviewNode) => {
    let errors = {} as any

    if (data.typeOfQuestion === QuestionType.ConfirmationQuestion) {

      if (!data.typeOfQuestion) {
        errors.typeOfQuestion = 'The type of question must be specified'
      }

      if (!data.name) {
        errors.name = 'Please specify the name of the node'
      } else if (!isUpdate && data.name) {
        if (intentNameList && intentNameList.length > 0 && intentNameList.find(element => element === data.name) !== undefined) {
          errors.name = 'This name is currently in use by another node'
        }
      }
      if (data.slotPrompt === undefined || data.slotPrompt.length === 0) {
        errors.questionPrompts = "Please input at least one confirmation prompt."
      }

      //Validation for only allowing 1 first and 1 last node
      if (data.isFirstQuestion && Object.keys(firstNode).length !== 0 && !initialValues.isFirstQuestion)
        errors.isFirstQuestion = "First node already exists"
      if (data.isLastQuestion && Object.keys(lastNode).length !== 0  && !initialValues.isLastQuestion)
        errors.isLastQuestion = "Last node already exists"
      // if (data.confirmationSetting?.questionPrompts.length >= 3) {
      //   errors.questionPrompts = "Only up to three prompts are allowed."
      // }
    }
    return errors;
  }

  const onSubmit = (data: any, form: any) => {
    let payload = {} as InterviewNode
    payload = data
    payload.position = data.position ? data.position : { x: 0, y: 0 }
    if (payload.typeOfQuestion === QuestionType.ConfirmationQuestion) { //Type narrowing
      if (payload.scoreIfNo === undefined)
        payload.scoreIfNo = 0; //Programmatically mutating score to 0 if user left InputText field blank
      else
        payload.scoreIfNo = +payload.scoreIfNo; //Parse InputText component from string to number type
      if (payload.scoreIfYes === undefined)
        payload.scoreIfYes = 0;
      else
        payload.scoreIfYes = +payload.scoreIfYes;
    }
    
    console.log(payload);
    dispatch(actionOnSubmit(payload, autosave))
    
    // setChips([])
    form.restart()
  }

  const deleteNode = (form: any) => {
    dispatch(actionOnDelete(initialValues, autosave))
    form.restart()
  }

  const getExcludedNameList = (values: any) => {
    if (!!values) {
      let tempIntentNameList = intentNameList?.slice(0)
      if (tempIntentNameList) {
        let valueIndex = tempIntentNameList.indexOf(values)
        if (valueIndex !== -1)
          tempIntentNameList.splice(valueIndex, 1)
      }
      return tempIntentNameList
    }
    return intentNameList
  }

  const required = (value: any) => value ? undefined : 'This is a required field'

  const isFormFieldValid = (meta: any) => !!((meta.touched || meta.modified) && meta.error)
  const getFormErrorMessage = (meta: any) => {
    return isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>
  }

  const WhenLastQuestionIsChecked = ({ set, to }: { set: string, to: any }) => {
    return (
      <Field name={set} subscription={{}}>
        {(
          // No subscription. We only use Field to get to the change function
          { input: { onChange } } 
        ) => {
          return (
            <OnChange name='isLastQuestion'>
              {(value: boolean) => {
                if (value === true) {
                  onChange(to);
                }
              }}
            </OnChange>
          )
        }}
      </Field>
    )
  }

  return (
    <div>
      <h1>{isUpdate ? "Update Confirmation Node" : "Create Confirmation Node"}</h1>
      <p className="text-sm font-italic font-normal line-height-3 text-500 hover:text-700">
        A Confirmation node is intended to capture a typical Yes or No response from a candidate.
        <br></br>
        Such as when given the question "Do you have any working experience?", a candidate would respond typically with "Yes", or "No".
      </p>
      <Form onSubmit={onSubmit} mutators={{ ...arrayMutators }} initialValues={initialValues} validate={validate} render={({ form: { mutators: { push, pop, remove } }, form, values, handleSubmit, pristine }) => (
        <form onSubmit={handleSubmit}>
          <WhenLastQuestionIsChecked set="confirmationSetting.flowToIfNo" to={undefined} />
          <WhenLastQuestionIsChecked set="confirmationSetting.flowToIfYes" to={undefined} />

          <Fieldset className={styles.nodeform} legend="Node Details" toggleable>
            <div className="formgrid grid">
              <Field name="typeOfQuestion" render={({ input, meta }) => (
                <div className="field col-6 md:col-6">
                  <label htmlFor="typeOfQuestionInput" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Type of Question*</label>
                  <Dropdown id="typeOfQuestionInput" {...input} disabled={isUpdate} options={Object.values(QuestionType)} onChange={onQuestionTypeChange} className={classNames("inputfield w-full", { 'p-invalid': isFormFieldValid(meta) })} />
                  {getFormErrorMessage(meta)}
                </div>
              )} />
              <Field name="name" render={({ input, meta }) => (
                <div className="field col-6 md:col-6">
                  <label htmlFor="nameInput" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Name of Node*</label>
                  <InputText id="nameInput" autoComplete="off" {...input} inputMode="text" className={classNames("inputfield w-full", { 'p-invalid': isFormFieldValid(meta) })} />
                  {getFormErrorMessage(meta)}
                </div>
              )} />
              <Field name="scoreIfNo" render={({ input }) => (
                <div className="field col-6 md:col-6">
                  <label htmlFor="scoreIfNoInput">Score if Unfulfilled</label>
                  <InputNumber id="scoreIfNoInput" min={0} max={5} tooltip="The score to be given for the candidate for fulfilling this branch condition" {...input} className="inputfield w-full" />
                </div>
              )} />
              <Field name="scoreIfYes" render={({ input }) => (
                <div className="field col-6 md:col-6">
                  <label htmlFor="scoreIfYesInput">Score if Fulfilled</label>
                  <InputNumber id="scoreIfYesInput" min={0} max={5} tooltip="The score to be given for the candidate for fulfilling this branch condition" {...input} className="inputfield w-full" />
                </div>
              )} />
              <Field name="isFirstQuestion" type="checkbox" render={({ input, meta }) => (
                <div className="field-checkbox field col-12 md:col-6">
                  <Checkbox inputId="isFirstQuestionInput" {...input} checked={input.checked || false} className={classNames({'p-error': isFormFieldValid(meta)})}/>
                  <label htmlFor="isFirstQuestionInput">Is this node the first question of the interview?</label> 
                  {getFormErrorMessage(meta)}
                </div>
              )} />
              <Field name="isLastQuestion" type="checkbox" render={({ input, meta }) => (
                <div className="field-checkbox field col-12 md:col-6">
                  <Checkbox inputId="isLastQuestionInput" {...input} checked={input.checked || false} className={classNames({'p-error': isFormFieldValid(meta)})}/>
                  <label htmlFor="isLastQuestionInput">Is this node the last question of the interview?</label>
                  {getFormErrorMessage(meta)}
                </div>
              )} />
            </div>
          </Fieldset>

          <Fieldset className={styles.nodeform} legend="Confirmation Prompt" toggleable>
            <Field name="slotPrompt" render={({ input, meta }) => (
              <div className="field col-12">
                <label htmlFor="slotPromptInput" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Slot Prompt*</label>
                <InputText id="slotPromptInput" {...input} inputMode="text" className={classNames("inputfield w-full", { 'p-invalid': isFormFieldValid(meta) })} />
                {getFormErrorMessage(meta)}
              </div>
            )} />
          </Fieldset>

          <Fieldset className={styles.nodeform} legend="On Confirmation" toggleable>
            <div className="formgrid grid">
              <Field name="confirmationSetting.flowToIfYes" render={({ input }) => (
                <div className="col-12">
                  <div className="field col-12">
                    <label htmlFor="flowToIfYes">Confirmation Step</label>
                    <Dropdown id="flowToIfYes" showClear disabled={values.isLastQuestion} tooltip="The next node to flow to if confirmed" {...input} options={!!intentNameList ? getExcludedNameList(values.name) : []} className="inputfield w-full" />
                  </div>
                </div>
              )} />
            </div>
          </Fieldset>

          <Fieldset className={styles.nodeform} legend="On Declination" toggleable>
            <div className="formgrid grid">
              <Field name="confirmationSetting.flowToIfNo" render={({ input }) => (
                <div className="col-12">
                  <div className="field col-12">
                    <label htmlFor="flowToIfNo">Declination Step</label>
                    <Dropdown id="flowToIfNo" showClear disabled={values.isLastQuestion} tooltip="The next node to flow to if the condition of this node is declined" {...input} options={!!intentNameList ? getExcludedNameList(values.name) : []} className="inputfield w-full" />
                  </div>
                </div>
              )} />
            </div>
          </Fieldset>
          <div className="flex justify-content-end align-items-end mt-2">
            <Button type="button" className="p-button-outlined p-button-danger mr-3" label="Delete" icon="pi pi-trash" onClick={() => deleteNode(form)} disabled={!isUpdate}></Button>
            <Button type="submit" label={isUpdate ? "Update" : "Create"} icon="pi pi-check" disabled={pristine}></Button>
          </div>
        </form >
      )
      } />
    </div >
  )
}

export default ConfirmationForm